// Utility helpers to display server-provided date/time strings WITHOUT applying timezone conversions.
// The server already returns the correct local date/time for display purposes. We must avoid new Date(...)
// which can shift the day when the client machine is in a different timezone.

// Accepted input examples:
//  - 2026-11-09 21:00:00+00
//  - 2026-11-09 21:00:00-03
//  - 2026-11-09 21:00:00
//  - 2026-11-09T21:00:00Z
//  - 2026-11-09T21:00:00-03:00

interface DateTimeParts {
  date: string; // yyyy-mm-dd
  time: string; // HH:mm (minutes only)
}

function extractParts(raw?: string | null): DateTimeParts | null {
  if (!raw) return null;
  // Normalize separator to space for easier splitting
  const normalized = raw.replace('T', ' ');
  const [datePart, rest] = normalized.split(' ');
  if (!datePart) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;

  let time = '';
  if (rest) {
    // Capture HH:MM from the beginning regardless of seconds, millis or timezone
    // Examples:
    // 21:00:00.000Z -> match 21:00
    // 21:00:00Z     -> 21:00
    // 21:00:00+00:00 -> 21:00
    // 21:00:00-03    -> 21:00
    // 21:00          -> 21:00
    const m = rest.match(/^([0-9]{2}:[0-9]{2})/);
    if (m) time = m[1];
  }
  return { date: datePart, time };
}

const MONTHS_SHORT_PT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

export function formatTrustedDate(raw?: string | null): string {
  const parts = extractParts(raw);
  if (!parts) return 'Data a definir';
  const [y, m, d] = parts.date.split('-');
  const monthName = MONTHS_SHORT_PT[Number(m) - 1];
  return `${Number(d)} de ${monthName} de ${y}`;
}

export function formatTrustedDateShort(raw?: string | null): string {
  const parts = extractParts(raw);
  if (!parts) return '—';
  const [y, m, d] = parts.date.split('-');
  return `${d}/${m}/${y}`; // dd/mm/yyyy
}

export function formatTrustedTime(raw?: string | null): string {
  const parts = extractParts(raw);
  if (!parts || !parts.time) return '—';
  return parts.time; // HH:mm
}

export function getDateTimeParts(raw?: string | null): DateTimeParts | null {
  return extractParts(raw);
}
